import { isPlaidConnection } from "../../helpers/financialConnection";
import { assertRelationLoaded } from "../../helpers/orm";
import { FinancialAccountSourceType } from "../../types/financialAccount";
import {
    ConnectionProviderType,
    FinancialConnection,
    FinancialConnectionBase,
} from "../../types/financialConnection";
import {
    IntegrationAccount,
    IntegrationAccountBase,
} from "../../types/integrationAccount";
import { isoStringOrNull, unpackDateOrNull, valueOrNull } from "../dto-helpers";
import { PlaidConnectionDto } from "../plaidConnection/plaid-connection.dto";
import { FinancialDocumentDto } from "../financialDocument/financial-document.dto";
import { IntegrationConnectionDto } from "../integrationConnection/integration-connection.dto";
import { FinancialAccountDto } from "./financial-account.dto";
import { FinancialConnectionDto } from "./financial-connection.dto";

/**
 * Input type for IntegrationAccountDto constructor.
 * Accepts Base type from entity classes - relations validated at runtime.
 */
export type IntegrationAccountDtoInput = IntegrationAccountBase;

interface IntegrationAccountDtoOptions {
    nestedInFinancialAccount?: boolean;
}

export class IntegrationAccountDto {
    static deserialize(dto: IntegrationAccountDto): IntegrationAccount {
        const connectionDeserialization =
            dto.connection?.connectionProvider === ConnectionProviderType.PLAID
                ? PlaidConnectionDto.deserialize
                : FinancialConnectionDto.deserialize;

        return {
            ...dto,
            balanceSyncedAt: unpackDateOrNull(dto.balanceSyncedAt),
            transactionsSyncedAt: unpackDateOrNull(dto.transactionsSyncedAt),
            invoicesSyncedAt: unpackDateOrNull(dto.invoicesSyncedAt),
            statementsSyncedAt: unpackDateOrNull(dto.statementsSyncedAt),
            lastStatementDate: dto.lastStatementDate,
            createdAt: unpackDateOrNull(dto.createdAt),
            financialAccount:
                dto.financialAccount &&
                FinancialAccountDto.deserialize(dto.financialAccount),
            financialDocument: dto.financialDocument
                ? FinancialDocumentDto.deserialize(dto.financialDocument)
                : undefined,
            connection: dto.connection
                ? valueOrNull(
                      dto.connection,
                      connectionDeserialization as (
                          dto: FinancialConnectionDto,
                      ) => FinancialConnection,
                  )
                : null,
            integrationConnection: dto.integrationConnection
                ? IntegrationConnectionDto.deserialize(
                      dto.integrationConnection,
                  )
                : undefined,
        };
    }
    id: string;
    balanceSyncedAt: string | null;
    transactionsSyncedAt: string | null;
    invoicesSyncedAt: string | null;
    statementsSyncedAt: string | null;
    lastStatementDate: string | null;
    createdAt: string | null;
    sourceType: FinancialAccountSourceType;
    sourceAccountId: string | null;
    connectionId: number | null;
    connection?: FinancialConnectionDto;
    integrationConnectionId: string | null;
    integrationConnection?: IntegrationConnectionDto;
    financialAccountId: number | null;
    financialAccount: FinancialAccountDto | null;
    financialDocument?: FinancialDocumentDto;
    metadata: any;

    constructor(
        integrationAccount: IntegrationAccountDtoInput,
        { nestedInFinancialAccount }: IntegrationAccountDtoOptions = {},
    ) {
        this.id = integrationAccount.id;
        this.balanceSyncedAt = isoStringOrNull(
            integrationAccount.balanceSyncedAt,
        );
        this.transactionsSyncedAt = isoStringOrNull(
            integrationAccount.transactionsSyncedAt,
        );
        this.invoicesSyncedAt = isoStringOrNull(
            integrationAccount.invoicesSyncedAt,
        );
        this.statementsSyncedAt = isoStringOrNull(
            integrationAccount.statementsSyncedAt,
        );
        this.lastStatementDate = integrationAccount.lastStatementDate;
        this.createdAt = isoStringOrNull(integrationAccount.createdAt);
        this.sourceType = integrationAccount.sourceType;
        this.sourceAccountId = integrationAccount.sourceAccountId;
        this.connectionId = integrationAccount.connectionId;
        if (integrationAccount.connection) {
            this.connection = this.serializeConnection(
                integrationAccount.connection,
            );
        }
        this.integrationConnection = integrationAccount.integrationConnection
            ? new IntegrationConnectionDto(
                  integrationAccount.integrationConnection,
              )
            : undefined;
        this.integrationConnectionId =
            integrationAccount.integrationConnectionId;
        this.financialAccountId = integrationAccount.financialAccountId;
        this.financialAccount = integrationAccount.financialAccount
            ? (() => {
                  const fa = integrationAccount.financialAccount!;
                  assertRelationLoaded(fa, "entity");
                  return new FinancialAccountDto(fa, {
                      includeIntegrationAccounts: !nestedInFinancialAccount,
                  });
              })()
            : null;
        this.financialDocument = integrationAccount.financialDocument
            ? new FinancialDocumentDto(integrationAccount.financialDocument)
            : undefined;

        this.metadata = integrationAccount.metadata;
    }

    serializeConnection(
        connection: FinancialConnectionBase,
    ): FinancialConnectionDto {
        if (isPlaidConnection(connection)) {
            return new PlaidConnectionDto(connection);
        }

        return new FinancialConnectionDto(connection);
    }
}
